// =====================================================
// Chat Input - Text Input & Send Button
// =====================================================

import React, { useState, useEffect } from 'react';
import { useChatStore } from '../store/chatStore';
import { sendMessage } from '../services/api';

// Rotating hint questions to guide users
const hintQuestions = {
  en: [
    'Ask me anything...',
    'I would like to buy a home in Lubbock.',
    'Show me the 3 bed cheapest home in Burgamy Park.',
    'What is the biggest floor plan in Lubbock?',
    'What is the cheapest home in Midland?',
    'What is the most affordable home in Amarillo?',
    'Show me homes near Frenship ISD.',
    'Communities in Lubbock South.',
    'Show me a 3 bed home in Odessa under $250K.',
    'Calculate mortgage.',
    'Cost of waiting.',
    'About the builder.',
    'Do you have move-in ready homes?',
    'Request a Tour.',
    'What is use it your way cash?',
    'What communities are in Lubbock?',
    'Do you offer financing options?',
    'Energy-efficient features.',
    'Floor plans to build in Harvest.',
    'I would like to schedule an appointment.',
  ],
  es: [
    'Pregúntame lo que quieras...',
    'Me gustaría comprar una casa en Lubbock.',
    'Muéstrame la casa más barata de 3 habitaciones en Burgamy Park.',
    '¿Cuál es el plano más grande en Lubbock?',
    '¿Cuál es la casa más barata en Midland?',
    '¿Cuál es la casa más económica en Amarillo?',
    'Muéstrame casas cerca de Frenship ISD.',
    'Comunidades en Lubbock Sur.',
    'Muéstrame una casa de 3 habitaciones en Odessa por menos de $250K.',
    'Calcular hipoteca.',
    'Costo de esperar.',
    'Sobre el constructor.',
    '¿Tienen casas listas para mudarse?',
    'Solicitar una visita.',
    '¿Qué es el efectivo úsalo a tu manera?',
    '¿Qué comunidades hay en Lubbock?',
    '¿Ofrecen opciones de financiamiento?',
    'Características de eficiencia energética.',
    'Planos para construir en Harvest.',
    'Me gustaría programar una cita.',
  ],
};

const ChatInput: React.FC = () => {
  const [inputValue, setInputValue] = useState('');
  const [hintIndex, setHintIndex] = useState(0);
  const {
    sessionId,
    userId,
    language,
    currentPage,
    messages,
    addMessage,
    setIsLoading,
    setSessionId
  } = useChatStore();

  // Rotate hint questions every 4 seconds
  useEffect(() => {
    const hints = hintQuestions[language] || hintQuestions.en;
    const interval = setInterval(() => {
      setHintIndex((prev) => (prev + 1) % hints.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [language]);

  // Get current hint based on language
  const currentHint = (hintQuestions[language] || hintQuestions.en)[hintIndex];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const message = inputValue.trim();
    if (!message) return;

    // Add user message
    addMessage({
      id: `msg_${Date.now()}`,
      sender: 'user',
      text: message,
      timestamp: new Date()
    });

    setInputValue('');
    setIsLoading(true);

    try {
      // Send to backend
      const response = await sendMessage({
        session_id: sessionId,
        user_id: userId,
        message,
        language,
        current_url: currentPage
      });

      // Save session ID if new
      if (response.session_id && !sessionId) {
        setSessionId(response.session_id);
      }

      // Add bot response
      addMessage({
        id: `msg_${Date.now() + 1}`,
        sender: 'bot',
        text: response.message,
        timestamp: new Date(),
        buttons: response.buttons,
        card: response.card,
        cards: response.cards,  // Include cards array for carousel
        dropdown: response.dropdown,  // Include dropdown options
        showAppointmentForm: response.showAppointmentForm  // Include appointment form flag
      });
    } catch (error) {
      console.error('Error sending message:', error);
      addMessage({
        id: `msg_${Date.now() + 1}`,
        sender: 'bot',
        text: language === 'en' 
          ? 'Sorry, I encountered an error. Please try again.' 
          : 'Lo siento, encontré un error. Por favor, inténtalo de nuevo.',
        timestamp: new Date()
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Check if user has interacted (more than just the welcome message)
  const hasUserInteracted = messages.some(msg => msg.sender === 'user');

  return (
    <form
      onSubmit={handleSubmit}
      className="p-3 sm:p-4 bg-betenbough-alabaster
                 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
    >
      {/* Privacy notice - shown only before first user interaction */}
      {!hasUserInteracted && (
        <div className="text-center mb-2">
          <p className="text-[10px] text-betenbough-gunmetal/60">
            {language === 'es' ? (
              <>
                Impulsado por IA. Al continuar, reconoces y aceptas nuestra{' '}
                <a
                  href="https://www.betenbough.com/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-betenbough-olive"
                >
                  Política de Privacidad
                </a>
              </>
            ) : (
              <>
                Powered by AI. By continuing, you acknowledge and agree to our{' '}
                <a
                  href="https://www.betenbough.com/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-betenbough-olive"
                >
                  Privacy Policy
                </a>
              </>
            )}
          </p>
        </div>
      )}

      <div className="flex items-center">
        <div className="relative flex-1">
          {/* Hint icon - only visible when input is empty and not "Ask me anything" */}
          {!inputValue && hintIndex !== 0 && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
              <svg
                className="w-4 h-4 text-betenbough-myrtle opacity-50"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                />
              </svg>
            </div>
          )}
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder={currentHint}
            className={`w-full py-2 border border-betenbough-myrtle rounded-full
                       text-betenbough-gunmetal text-sm placeholder-betenbough-myrtle placeholder-opacity-50
                       focus:outline-none focus:border-betenbough-olive
                       focus:ring-2 focus:ring-betenbough-citron focus:ring-opacity-30
                       font-filson transition-all ${!inputValue && hintIndex !== 0 ? 'pl-9' : 'pl-4'} ${inputValue.trim() ? 'pr-10' : 'pr-4'}`}
          />
          {/* Send button - only visible when there's text */}
          {inputValue.trim() && (
            <button
              type="submit"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 w-7 h-7 bg-betenbough-citron
                         text-betenbough-gunmetal rounded-full flex items-center justify-center
                         hover:bg-betenbough-olive transition-all active:scale-95"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5}
                      d="M12 19V5M5 12l7-7 7 7" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </form>
  );
};

export default ChatInput;